import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Sidebar from "../Sidebar/Sidebar";
import "./UpdatePharmacyBranchProfile.css";
import { cloudApi } from "../../api";

const GOOGLE_API_KEY = process.env.REACT_APP_GOOGLE_API_KEY;

const UpdatePharmacyBranchProfile = ({ branchId }) => {
  const [branch, setBranch] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [hasFetched, setHasFetched] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const fetchBranchProfile = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await cloudApi.get(`/users/pharmacy/branch/${branchId}/profile`);
      const data = response.data;
      setBranch({
        branch_name: data.branch_name || "",
        branch_address: data.branch_address || "",
        opening_hours: data.opening_hours || "",
        supervising_pharmacist_name: data.supervising_pharmacist_name || "",
        supervising_pharmacist_license: data.supervising_pharmacist_license || "",
        city: data.city || "",
        state: data.state || "",
        country: data.country || "",
        latitude: data.latitude ?? 0,
        longitude: data.longitude ?? 0,
      });
      setError("");
    } catch (err) {
      console.error("❌ Fetch error:", err);
      setError("Failed to load branch profile.");
    } finally {
      setIsLoading(false);
    }
  }, [branchId]);

  useEffect(() => {
    if (!hasFetched) {
      fetchBranchProfile();
      setHasFetched(true);
    }
  }, [fetchBranchProfile, hasFetched]);

  // ── Auto-geocoding ──
  useEffect(() => {
    if (!branch) return;
    const timeout = setTimeout(async () => {
      if (branch.branch_address && branch.city && branch.country) {
        const fullAddress = `${branch.branch_address}, ${branch.city}, ${branch.country}`;
        try {
          const geo = await axios.get(
            "https://maps.googleapis.com/maps/api/geocode/json",
            { params: { address: fullAddress, key: GOOGLE_API_KEY } }
          );
          const results = geo?.data?.results;
          if (results?.length > 0) {
            const loc = results[0].geometry.location;
            setBranch((prev) => ({
              ...prev,
              latitude: parseFloat(loc.lat.toFixed(6)),
              longitude: parseFloat(loc.lng.toFixed(6)),
            }));
          }
        } catch (err) {
          console.error("❌ Geocoding failed:", err);
        }
      }
    }, 800);
    return () => clearTimeout(timeout);
  }, [branch?.branch_address, branch?.city, branch?.country]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setBranch((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccessMessage("");
    setUpdating(true);
    try {
      const payload = {
        branch_name: branch.branch_name,
        branch_address: branch.branch_address,
        opening_hours: branch.opening_hours,
        supervising_pharmacist_name: branch.supervising_pharmacist_name,
        supervising_pharmacist_license: branch.supervising_pharmacist_license,
        city: branch.city,
        state: branch.state,
        country: branch.country,
        latitude: branch.latitude,
        longitude: branch.longitude,
      };
      await cloudApi.put(`/users/users/pharmacy/branch/${branchId}/profile`, payload);
      setSuccessMessage("✅ Branch profile updated successfully!");
      setTimeout(() => { navigate(`/pharmacy/branch/${branchId}`); }, 1500);
    } catch (err) {
      console.error("❌ Update failed:", err);
      let errMsg = "Failed to update branch profile.";
      if (err.response?.data?.detail) {
        const detail = err.response.data.detail;
        if (Array.isArray(detail)) errMsg = detail.map((e) => e.msg).join(", ");
        else if (typeof detail === "string") errMsg = detail;
      }
      setError(`❌ ${errMsg}`);
    } finally {
      setUpdating(false);
    }
  };

  if (isLoading) {
    return (
      <div className="pbp-page-container">
        {!isMobile && <Sidebar />}
        <div className="pbp-content">
          <div className="loading-container">
            <div className="spinner"></div>
            <p>Loading branch profile...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pbp-page-container">
      {!isMobile && <Sidebar />}
      <div className="pbp-content">
        <h2 className="pbp-title">Update Branch Profile</h2>

        {error && !successMessage ? (
          <p className="pbp-error">{error}</p>
        ) : (
          <form className="pbp-card" onSubmit={handleSubmit}>
            <label>
              Branch Name
              <input type="text" name="branch_name" value={branch.branch_name} onChange={handleChange} required />
            </label>
            <label>
              Branch Address
              <input type="text" name="branch_address" value={branch.branch_address} onChange={handleChange} />
            </label>
            <label>
              Opening Hours
              <input type="text" name="opening_hours" value={branch.opening_hours} onChange={handleChange} />
            </label>
            <label>
              Supervising Pharmacist Name
              <input type="text" name="supervising_pharmacist_name" value={branch.supervising_pharmacist_name} onChange={handleChange} />
            </label>
            <label>
              Supervising Pharmacist License
              <input type="text" name="supervising_pharmacist_license" value={branch.supervising_pharmacist_license} onChange={handleChange} />
            </label>
            <label>
              City
              <input type="text" name="city" value={branch.city} onChange={handleChange} />
            </label>
            <label>
              State
              <input type="text" name="state" value={branch.state} onChange={handleChange} />
            </label>
            <label>
              Country
              <input type="text" name="country" value={branch.country} onChange={handleChange} />
            </label>

            <div className="pbp-btn-group">
              <button type="submit" disabled={updating}>
                {updating ? "Updating..." : "Update"}
              </button>
            </div>

            {successMessage && <p className="pbp-success">{successMessage}</p>}
            {error && <p className="pbp-error">{error}</p>}
          </form>
        )}
      </div>
    </div>
  );
};

export default UpdatePharmacyBranchProfile;